interface IRangeValue {
  name: string;
  type: string | 'range' | 'more' | 'less' | 'exact';
  values: number[];
  key: string;
}

/**
 * Create a values range from a given min and max value
 */
export function getFilterRangesByList(
  elements: number[],
  options: {
    symbol: string | undefined;
  },
): IRangeValue[] {
  const range: IRangeValue[] = [];

  // Order the elements
  const orderedElements = elements.sort((a, b) => a - b);

  // remove duplicates from the array
  let uniqueElements = orderedElements.filter((element, index, array) => {
    return index === array.indexOf(element);
  });

  const formatter = Intl.NumberFormat(
    'en',
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //@ts-ignore
    { notation: 'compact', maximumSignificantDigits: 4 },
  );
  const total = uniqueElements.length;

  if (total <= 5) {
    // add lineal ranges
    for (let i = 0; i < total; i++) {
      const element = uniqueElements[i];
      const elementName = element > 1000 ? formatter.format(element) : element;
      // add values to the range
      range.push({
        name: `${options.symbol ?? ''}${elementName}`,
        type: 'exact',
        values: [element],
        key: `${element}:${element}`,
      });
    }

    return range;
  }

  // remove 0 values
  uniqueElements = uniqueElements
    .filter((element) => element !== 0)
    .map((element) => Math.ceil(element));

  const limit = 5;
  const steps = Math.round(total / limit);
  const stepsCount = uniqueElements.length > 5 ? limit : 4;

  for (let i = 0; i < stepsCount; i++) {
    // calculate the index
    const i1 = i * steps;
    const i2 = (i + 1) * steps;

    // get the values
    const element = uniqueElements[i1];

    if (i === 0) {
      // if the range if the first value then add the less text
      const next = uniqueElements[i2];
      range.push({
        name: `Less than ${options.symbol ?? ''}${formatter.format(
          Number(next),
        )}`,
        type: 'less',
        values: [next],
        key: `less:${next}`,
      });
    } else if (i + 1 >= stepsCount) {
      // if the range if the last value then add the more text
      const next = Number(element ?? uniqueElements[uniqueElements.length - 1]);

      range.push({
        name: `More than ${options.symbol ?? ''}${formatter.format(next)}`,
        type: 'more',
        values: [next],
        key: `more:${next}`,
      });
    } else {
      const next =
        uniqueElements[i2] ?? uniqueElements[uniqueElements.length - 1];
      range.push({
        name: `${options.symbol ?? ''}${formatter.format(Number(element))} - ${
          options.symbol ?? ''
        }${formatter.format(Number(next))}`,
        type: 'range',
        values: [element, next],
        key: `${element}:${next}`,
      });
    }
  }

  return range;
}

/**
 * @param {T[]} array
 * @returns: shuffled array
 * @description Fisher-Yates shuffle algorithm O(n)
 */
export function shuffle<T>(array: T[]): T[] {
  let currentIndex = array.length,
    randomIndex;

  while (currentIndex != 0) {
    randomIndex = Math.floor(Math.random() * currentIndex);
    currentIndex--;

    [array[currentIndex], array[randomIndex]] = [
      array[randomIndex],
      array[currentIndex],
    ];
  }

  return array;
}

/**
 * @param {T[]} array
 * @returns: Group array by key
 * @description Takes in an array of items and a key to group by.
 */
export function groupBy<T>(arr: T[], fn: (item: T) => any) {
  return arr.reduce<Record<string, T[]>>((prev, curr) => {
    const groupKey = fn(curr);
    const group = prev[groupKey] || [];
    group.push(curr);
    return { ...prev, [groupKey]: group };
  }, {});
}
